import { useState } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@finn-auto/design-system/atoms/tooltip';
import { BAR_RADIUS, MAX_BAR_THICKNESS, formatCompact } from './chartTokens';

export interface RankedItem {
  label: string;
  value: number;
  /**
   * Plain-language explanation of what this row means, shown on hover/focus.
   *
   * Optional because not every ranked list needs one — a category called "Compensation" explains
   * itself, an event called `ta_requests_draft_rated` does not.
   */
  hint?: string;
}

/**
 * Ranked horizontal bars — the form for "compare magnitude" when the categories have long names.
 *
 * Horizontal because event names like `ta_requests_draft_save_failed` cannot be read rotated under
 * a column, and one hue rather than a categorical palette because these bars are one measure
 * ordered by size: colouring them by rank would make the colour change whenever the ranking does,
 * which reads as meaning that is not there.
 *
 * Every value is directly labelled at the tip, so the hover tooltip enhances rather than gates —
 * nothing here is reachable only by pointing at it.
 */
export function RankedBars({
  items,
  color,
  emptyMessage = 'Nothing recorded in this range yet.',
  max: sharedMax,
}: {
  items: RankedItem[];
  color: string;
  emptyMessage?: string;
  /**
   * Scale bars against this instead of the tallest item in THIS list. For several lists shown
   * together, where a bar has to mean the same thing in each of them.
   */
  max?: number;
}) {
  const [hovered, setHovered] = useState<string | null>(null);

  if (items.length === 0) {
    return <p className="py-6 text-sm text-zinc-500">{emptyMessage}</p>;
  }

  const max = sharedMax || items.reduce((acc, item) => Math.max(acc, item.value), 0) || 1;

  return (
    <ul className="space-y-2.5">
      {items.map((item) => {
        const pct = (item.value / max) * 100;
        const isHovered = hovered === item.label;
        return (
          <li
            key={item.label}
            // The hit target is the whole row, not the painted bar: a short bar is a sliver, and
            // a target you have to aim at is a target most people miss.
            className="group cursor-default rounded-md px-1 py-0.5 transition-colors hover:bg-zinc-50"
            onPointerEnter={() => setHovered(item.label)}
            onPointerLeave={() => setHovered(null)}
            onFocus={() => setHovered(item.label)}
            onBlur={() => setHovered(null)}
            tabIndex={0}
          >
            <div className="flex items-baseline justify-between gap-3">
              {/*
                The dotted underline is the affordance: without it an explanation that only appears
                on hover is an explanation nobody knows to look for. Rows with no hint keep the
                plain label rather than advertising something that is not there.
              */}
              {item.hint ? (
                <Tooltip>
                  <TooltipTrigger>
                    <span
                      className="cursor-help truncate font-mono text-xs text-zinc-600 underline decoration-zinc-300 decoration-dotted underline-offset-4"
                      title={item.label}
                    >
                      {item.label}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent body={item.hint} />
                </Tooltip>
              ) : (
                <span className="truncate font-mono text-xs text-zinc-600" title={item.label}>
                  {item.label}
                </span>
              )}
              <span
                className="shrink-0 text-xs font-semibold text-zinc-900"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatCompact(item.value)}
              </span>
            </div>
            <div
              className="mt-1 w-full overflow-hidden"
              style={{ height: MAX_BAR_THICKNESS / 3 }}
              role="presentation"
            >
              <div
                className="h-full transition-opacity"
                style={{
                  width: `${Math.max(pct, 1)}%`,
                  backgroundColor: color,
                  // Rounded at the data end, square at the baseline, so every bar grows from the
                  // same flat left edge.
                  borderRadius: `0 ${BAR_RADIUS}px ${BAR_RADIUS}px 0`,
                  opacity: isHovered ? 1 : 0.85,
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
